import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';

import { Button, cx } from '../design/index.js';
import { useDismissable } from './useDismissable.js';
import styles from './ChartViewMenu.module.css';

export interface ChartViewMenuProps {
  /**
   * The controls themselves.
   *
   * Passed in rather than named as props, because the state they read and
   * write belongs to the screen that owns the chart, and threading three
   * sliders' worth of value/onChange through here would only move that state
   * further from what uses it. This component is the disclosure and nothing
   * else: a button, a panel, and the behaviour a panel owes its reader.
   */
  children: ReactNode;
  className?: string;
}

/**
 * The chart's view settings, behind a button.
 *
 * They used to sit in the page header beside the current reading, where three
 * controls competed with the one number the page exists to show. They are
 * adjusted rarely and read constantly, which is the shape of a thing that
 * belongs behind a disclosure.
 *
 * Deliberately NOT `role="menu"`. That role is for a list of commands, and
 * its keyboard contract is arrow-keys-to-move — which would fight every
 * slider in here, where arrow keys mean "change the value". A plain labelled
 * group, where Tab moves between controls, is what these actually are.
 */
export function ChartViewMenu({ children, className }: ChartViewMenuProps) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const { triggerRef, panelRef } = useDismissable<HTMLButtonElement, HTMLDivElement>(open, close);

  return (
    <div className={cx(styles.wrap, className)}>
      <Button
        ref={triggerRef}
        variant="secondary"
        size="sm"
        leading={<SlidersHorizontal size={16} aria-hidden="true" />}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        data-testid="chart-view-trigger"
      >
        View
      </Button>

      {open && (
        <div
          ref={panelRef}
          className={styles.panel}
          /* Focusable so `useDismissable` has somewhere to put focus on
             open — otherwise it stays on the trigger, and the first Tab
             takes the reader past the panel instead of into it. */
          tabIndex={-1}
          role="group"
          aria-label="Chart view settings"
          data-testid="chart-view-panel"
        >
          {children}
        </div>
      )}
    </div>
  );
}
